import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import rafikiImg from '../../../assets/rafiki.svg';

function TermsOfService() {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box component="main" className="bg-white min-h-screen font-sans text-gray-900">

      {/* Header / Title Section */}
      <div className="bg-slate-50 border-b border-gray-200 py-16">
        <div className="container mx-auto px-6 lg:px-40 max-w-7xl">
          <h1 className="text-4xl lg:text-5xl font-bold text-secondary-500 mb-6 tracking-tight">
            Terms of Service – Role‑Specific Agreements
          </h1>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <p className="text-gray-600 font-medium mb-2">
                Last Updated: 8 January 2026
              </p>
              <p className="text-xl text-gray-800 leading-relaxed max-w-3xl">
                These Role‑Specific Terms of Service supplement the general Terms of Service of <span className="font-bold text-secondary-500">Oye Creators</span>, owned and operated by <span className="font-bold text-secondary-500">TTL Media Pvt. Ltd.</span> They apply in addition to the master Terms and are tailored for different categories of users on the Platform.
              </p>
            </div>
            {/* Image Section */}
            <div className="hidden md:block">
              <img src={rafikiImg} alt="Terms of Service" className="w-48 h-auto opacity-90 grayscale-[20%]" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 lg:px-40 max-w-7xl py-12">
        <div className="prose prose-lg max-w-none text-gray-800">

          {/* Section 1: Content Creators */}
          <section className="mb-16">
            <div className="flex items-center mb-6 pb-2 border-b border-gray-200">
              <span className="text-3xl mr-3">1️⃣</span>
              <h2 className="text-2xl font-bold text-secondary-500 uppercase tracking-wide my-0">Terms of Service for Content Creators</h2>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">1.1 Role & Platform Nature</h3>
                <p className="font-medium text-gray-800 leading-relaxed">
                  Content Creators are independent individuals or entities using Oye Creators to discover and participate in brand campaigns. Creators are <span className="underline decoration-red-500 decoration-2">not</span> employees, agents, or representatives of Oye Creators or brands.
                </p>
              </div>

              <div className="bg-gray-50 p-6 border-l-4 border-secondary-400 rounded-r-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">1.2 Account Responsibilities</h3>
                <p className="mb-2 font-bold text-gray-800">Creators must:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 mb-4 ml-2">
                  <li>Provide accurate and up‑to‑date profile information</li>
                  <li>Maintain only one account per individual/entity</li>
                  <li>Ensure authenticity of followers, engagement, and content</li>
                </ul>
                <p className="text-red-700 font-bold text-sm bg-red-50 inline-block px-3 py-1 rounded">
                  🚫 Fake profiles, purchased engagement, or impersonation are strictly prohibited.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">1.3 Campaign Participation</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-800 ml-2 font-medium">
                    <li>Campaign invitations are discretionary and subject to brand selection</li>
                    <li>Submission of content does not guarantee approval or payment</li>
                    <li>Creators must follow campaign briefs and timelines</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">1.4 Earnings & Payments</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-800 ml-2 font-medium">
                    <li>Earnings depend on campaign terms, approval, and performance</li>
                    <li>Oye Creators does not guarantee earnings or minimum payouts</li>
                    <li>Payments are processed through third‑party gateways after campaign completion and approval</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">1.5 Content Ownership & License</h3>
                <p className="font-medium text-gray-800 leading-relaxed">
                  Creators retain ownership of their content. By participating in campaigns, creators grant Oye Creators and the brand a limited, non‑exclusive license to use the content for campaign execution, reporting, and promotional purposes as agreed.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">1.6 Advertising & Disclosure</h3>
                <p className="font-medium text-gray-800 leading-relaxed mb-2">
                  Creators must comply with ASCI guidelines and applicable laws, clearly disclosing sponsored content using labels such as <span className="bg-gray-200 px-1 rounded text-sm text-gray-900">#Ad</span> or <span className="bg-gray-200 px-1 rounded text-sm text-gray-900">#Sponsored</span>.
                </p>
                <p className="text-sm text-red-600 font-bold">Failure to disclose may result in campaign removal, non‑payment, or account suspension.</p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">1.7 Prohibited Conduct</h3>
                <p className="mb-2 font-medium">Creators must not:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-800 ml-2 font-semibold">
                  <li>Manipulate engagement metrics</li>
                  <li>Post misleading or unlawful content</li>
                  <li>Violate third‑party intellectual property rights</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">1.8 Suspension & Termination</h3>
                <p className="font-medium text-gray-800 leading-relaxed">
                  Accounts may be suspended or terminated for policy violations, fraud, or repeated non‑compliance. Appeals may be submitted to <a href="mailto:support@oyecreators.com" className="text-primary-600 underline">support@oyecreators.com</a> within 14 days.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2: Vendors & Service Providers */}
          <section className="mb-16">
            <div className="flex items-center mb-6 pb-2 border-b border-gray-200">
              <span className="text-3xl mr-3">2️⃣</span>
              <h2 className="text-2xl font-bold text-secondary-500 uppercase tracking-wide my-0">Terms of Service for Vendors & Service Providers</h2>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">2.1 Role Definition</h3>
                <p className="font-medium text-gray-800 leading-relaxed">
                  Vendors include agencies, production partners, or service providers offering goods or services through Oye Creators.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">2.2 Independent Contractor Status</h3>
                <p className="mb-2 font-medium text-gray-800">Vendors operate as independent contractors and are solely responsible for:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-800 ml-2">
                  <li>Deliverables</li>
                  <li>Taxes and statutory compliance</li>
                  <li>Workforce and subcontractors</li>
                </ul>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">2.3 Listings & Representations</h3>
                  <p className="mb-2 font-medium text-gray-800">Vendors must ensure that:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-800 ml-2">
                    <li>Service descriptions are accurate</li>
                    <li>Pricing and timelines are transparent</li>
                    <li>No misleading claims are made</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">2.4 Payments & Invoicing</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-800 ml-2 font-medium">
                    <li>Payments are subject to agreed commercial terms</li>
                    <li>Oye Creators may facilitate payments but is not liable for vendor performance</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">2.5 & 2.6 Compliance & Liability</h3>
                <p className="font-medium text-gray-800 leading-relaxed mb-4">
                  Vendors must comply with all applicable laws, including intellectual property, advertising, and data protection regulations.
                </p>
                <div className="bg-amber-50 p-4 border border-amber-200 rounded">
                  <strong className="text-amber-800">Liability Notice:</strong>
                  <span className="text-amber-900 ml-2">Vendors are responsible for any claims arising from their services, including infringement, negligence, or breach of contract.</span>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">2.7 Termination</h3>
                <p className="font-medium text-gray-800 leading-relaxed">
                  Vendor access may be terminated for breach of terms, misconduct, or reputational risk to the Platform.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3: Enterprise Clients */}
          <section className="mb-16">
            <div className="flex items-center mb-6 pb-2 border-b border-gray-200">
              <span className="text-3xl mr-3">3️⃣</span>
              <h2 className="text-2xl font-bold text-secondary-500 uppercase tracking-wide my-0">Terms of Service for Enterprise Clients (Brands & Advertisers)</h2>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">3.1 Role Definition</h3>
                <p className="font-medium text-gray-800 leading-relaxed">
                  Enterprise Clients include brands, advertisers, and corporate entities engaging creators through the Platform.
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">3.2 Platform Limitation</h3>
                <p className="mb-2 font-medium text-gray-800">Oye Creators acts solely as a technology intermediary and does not:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-800 ml-2">
                  <li>Guarantee creator performance</li>
                  <li>Supervise content creation</li>
                  <li>Act as an agent or employer of creators</li>
                </ul>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3.3 Campaign Responsibility</h3>
                  <p className="mb-2 font-bold text-gray-700">Enterprise Clients are responsible for:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-800 ml-2">
                    <li>Campaign briefs and approval criteria</li>
                    <li>Compliance with advertising laws and ASCI guidelines</li>
                    <li>Timely feedback and approvals</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3.4 Payments & Fees</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-800 ml-2 font-medium">
                    <li>Campaign fees and platform charges are payable as agreed</li>
                    <li>Refunds are subject to campaign status and contractual terms</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">3.5 Intellectual Property</h3>
                <p className="font-medium text-gray-800 mb-2">Unless otherwise agreed:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-800 ml-2">
                  <li>Brands receive usage rights as defined per campaign</li>
                  <li>Creators retain ownership of original content</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">3.6 Compliance & Indemnity</h3>
                <p className="font-medium text-gray-800 leading-relaxed">
                  Enterprise Clients agree to indemnify Oye Creators against claims arising from: Misleading campaigns, Unlawful advertising, Third‑party rights violations.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">3.7 Suspension & Termination</h3>
                <p className="font-medium text-gray-800 leading-relaxed">
                  Oye Creators may suspend or terminate enterprise access for non‑payment, policy violations, or legal risk.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: General Provisions */}
          <section className="mb-14">
            <div className="flex items-center mb-6 pb-2 border-b border-gray-200">
              <span className="text-3xl mr-3">4️⃣</span>
              <h2 className="text-2xl font-bold text-secondary-500 uppercase tracking-wide my-0">General Provisions (All Roles)</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">4.1 Privacy & Data Protection</h3>
                <p className="text-gray-800 leading-relaxed">All users are subject to the Privacy & Cookies Policy. Users must process personal data lawfully and securely.</p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">4.2 Disclaimer</h3>
                <p className="text-gray-800 leading-relaxed">Services are provided on an "as is" and "as available" basis to the maximum extent permitted by law.</p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">4.3 Limitation of Liability</h3>
                <p className="text-gray-800 leading-relaxed">To the extent permitted by law, Oye Creators’ total liability shall not exceed the fees paid to access the Services.</p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">4.4 Governing Law & Jurisdiction</h3>
                <p className="text-gray-800 leading-relaxed">These Terms are governed by the laws of India. Courts in Pune, Maharashtra shall have exclusive jurisdiction.</p>
              </div>
            </div>
          </section>

          <hr className="my-12 border-gray-200" />

          {/* Contact Section */}
          <section className="mb-14 bg-gray-50 border border-gray-200 p-8 rounded-lg">
            <h2 className="text-2xl font-bold text-secondary-500 mb-6">4.5 Contact Information</h2>
            <div className="flex flex-col space-y-4">
              <div className="flex items-center">
                <span className="text-2xl mr-3">📧</span>
                <a href="mailto:support@oyecreators.com" className="text-lg font-bold text-primary-600 hover:underline">support@oyecreators.com</a>
              </div>
              <div className="flex items-center">
                <span className="text-2xl mr-3">🏢</span>
                <span className="text-lg font-medium text-gray-900">TTL Media Pvt. Ltd., Pune, Maharashtra</span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Box>
  );
}

export default TermsOfService;